import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, XCircle, ChevronDown, ChevronUp, AlertTriangle, Loader2 } from 'lucide-react';

export interface UnifiedCodeCardProps {
  code: string;
  description: string;
  confidence: number;
  source: 'imo' | 'imo-precision' | 'llm' | 'ai';
  category?: string;
  rationale?: string;
  evidenceText?: string;
  evidenceSource?: string;
  status: 'suggested' | 'pending' | 'approved' | 'rejected';
  codeType?: 'icd10' | 'cpt' | 'em';
  onApprove?: () => void;
  onReject?: (notes?: string) => void;
  onRemove?: () => void;
  requireRejectNotes?: boolean;
  showEvidence?: boolean;
  /** This card's approve is saving: show a spinner on the approve button */
  isApproving?: boolean;
  /** Disable approve/reject while another action is saving */
  actionsDisabled?: boolean;
  // V3 audit additions (optional — only present when V3 pipeline ran)
  qualityBadge?: 'high' | 'medium' | 'low';
  needsReview?: boolean;
  alternatives?: Array<{ code: string; description: string; score: number; why: string }>;
  onSwapToAlternative?: (code: string) => void;
}

/**
 * UnifiedCodeCard - Consistent code display component for all coding sections
 *
 * Features:
 * - Compact design that doesn't stretch the column
 * - Source badge (IMO/AI)
 * - Category badge
 * - Confidence percentage with color coding
 * - Truncated description (max 2 lines)
 * - Collapsible rationale/evidence
 * - Approve/Reject actions with animations
 */
export const UnifiedCodeCard: React.FC<UnifiedCodeCardProps> = ({
  code,
  description,
  confidence,
  source,
  category,
  rationale,
  evidenceText,
  evidenceSource,
  status,
  codeType,
  onApprove,
  onReject,
  onRemove,
  requireRejectNotes = false,
  showEvidence = false,
  isApproving = false,
  actionsDisabled = false,
  qualityBadge,
  needsReview,
  alternatives,
  onSwapToAlternative,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectNotes, setRejectNotes] = useState('');

  const getConfidenceColor = (conf: number) => {
    if (conf >= 0.8) return 'text-green-700 bg-green-50';
    if (conf >= 0.6) return 'text-yellow-700 bg-yellow-50';
    return 'text-red-700 bg-red-50';
  };

  const getSourceBadge = () => {
    const isImo = source === 'imo' || source === 'imo-precision';
    return isImo
      ? 'bg-blue-600 text-white'
      : 'bg-purple-100 text-purple-800';
  };

  const getSourceLabel = () => {
    if (source === 'imo' || source === 'imo-precision') return 'IMO';
    return 'AI';
  };

  const getStatusStyles = () => {
    switch (status) {
      case 'approved':
        return 'border-green-300 bg-green-50/50';
      case 'rejected':
        return 'border-red-300 bg-red-50/50 opacity-60';
      default:
        return 'border-gray-200 bg-white hover:border-gray-300';
    }
  };

  const handleRejectClick = () => {
    if (requireRejectNotes) {
      setShowRejectModal(true);
    } else {
      onReject?.();
    }
  };

  const handleRejectSubmit = () => {
    onReject?.(rejectNotes);
    setShowRejectModal(false);
    setRejectNotes('');
  };

  const hasExpandableContent = Boolean(rationale || (evidenceText && showEvidence) || (alternatives && alternatives.length > 0));
  const isPending = status === 'pending' || status === 'suggested';

  return (
    <>
      <motion.div
        layout
        initial={{ opacity: 0, y: 5 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, x: -20 }}
        className={`border rounded-lg p-2.5 transition-all ${getStatusStyles()}`}
      >
        {/* Main Row */}
        <div className="flex items-start gap-3">
          {/* Code Info */}
          <div className="flex-1 min-w-0">
            {/* Code Header Row */}
            <div className="flex items-center gap-1.5 flex-wrap">
              {/* Code */}
              <span className="font-mono font-bold text-base text-gray-900">
                {code}
              </span>

              {/* Source Badge */}
              <span className={`px-1.5 py-0.5 rounded text-xs font-semibold ${getSourceBadge()}`}>
                {getSourceLabel()}
              </span>

              {/* Category Badge */}
              {category && category !== 'treatment' && (
                <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-700 capitalize">
                  {category}
                </span>
              )}

              {/* Code Type Badge (for mixed lists) */}
              {codeType && (
                <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600 uppercase">
                  {codeType}
                </span>
              )}

              {/* Confidence */}
              <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${getConfidenceColor(confidence)}`}>
                {Math.round(confidence * 100)}%
              </span>

              {/* V3 Quality Badge */}
              {qualityBadge && (
                <span
                  className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-xs font-medium ${
                    qualityBadge === 'high'
                      ? 'bg-emerald-100 text-emerald-700'
                      : qualityBadge === 'medium'
                      ? 'bg-amber-100 text-amber-700'
                      : 'bg-red-100 text-red-700'
                  }`}
                  title={`Match quality: ${qualityBadge}`}
                >
                  {qualityBadge === 'high' ? (
                    <CheckCircle2 className="w-3 h-3" />
                  ) : (
                    <AlertTriangle className="w-3 h-3" />
                  )}
                  {qualityBadge.charAt(0).toUpperCase() + qualityBadge.slice(1)}
                </span>
              )}

              {/* V3 Needs Review */}
              {needsReview && (
                <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-xs font-semibold bg-amber-50 text-amber-800 border border-amber-200">
                  <AlertTriangle className="w-3 h-3" />
                  Review
                </span>
              )}

              {/* Status Badge for approved/rejected */}
              {status === 'approved' && (
                <span className="flex items-center gap-0.5 px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">
                  <CheckCircle2 className="w-3 h-3" />
                  Approved
                </span>
              )}
              {status === 'rejected' && (
                <span className="flex items-center gap-0.5 px-1.5 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700">
                  <XCircle className="w-3 h-3" />
                  Rejected
                </span>
              )}
            </div>

            {/* Description */}
            <p className="text-sm text-gray-700 mt-1" title={description}>
              {description}
            </p>

            {/* Expandable Content Toggle */}
            {hasExpandableContent && (
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="flex items-center gap-1 mt-1.5 text-xs text-blue-600 hover:text-blue-800 font-medium"
              >
                {isExpanded ? (
                  <>
                    <ChevronUp className="w-3 h-3" />
                    Hide details
                  </>
                ) : (
                  <>
                    <ChevronDown className="w-3 h-3" />
                    {rationale ? 'Show rationale' : 'Show evidence'}
                  </>
                )}
              </button>
            )}

            {/* Expanded Content */}
            <AnimatePresence>
              {isExpanded && hasExpandableContent && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="overflow-hidden"
                >
                  <div className="mt-2 pl-3 border-l-2 border-blue-300 py-1 space-y-2">
                    {rationale && (
                      <div>
                        <span className="text-xs font-semibold text-gray-500">Rationale:</span>
                        <p className="text-xs text-gray-600">{rationale}</p>
                      </div>
                    )}
                    {evidenceText && showEvidence && (
                      <div>
                        <span className="text-xs font-semibold text-gray-500">
                          Evidence {evidenceSource && `(${evidenceSource})`}:
                        </span>
                        <p className="text-xs text-gray-600 italic">"{evidenceText}"</p>
                      </div>
                    )}
                    {alternatives && alternatives.length > 0 && (
                      <div>
                        <span className="text-xs font-semibold text-gray-500">Other candidates:</span>
                        <ul className="mt-1 space-y-1">
                          {alternatives.map((alt) => (
                            <li key={alt.code} className="flex items-center gap-2 text-xs">
                              {onSwapToAlternative ? (
                                <button
                                  type="button"
                                  onClick={() => onSwapToAlternative(alt.code)}
                                  className="font-mono text-blue-700 hover:underline focus:outline-none"
                                  title={alt.why}
                                >
                                  {alt.code}
                                </button>
                              ) : (
                                <span className="font-mono text-gray-700" title={alt.why}>
                                  {alt.code}
                                </span>
                              )}
                              <span className="text-gray-600 truncate">— {alt.description}</span>
                              <span className="text-gray-400 shrink-0">
                                ({Math.round(alt.score * 100)}%)
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Action Buttons */}
          {isPending && (onApprove || onReject) && (
            <div className="flex gap-1.5 shrink-0">
              {onApprove && (
                <button
                  onClick={onApprove}
                  disabled={isApproving || actionsDisabled}
                  className={`p-2.5 min-h-[44px] min-w-[44px] flex items-center justify-center text-white bg-green-600 enabled:hover:bg-green-700 rounded transition-colors ${
                    isApproving ? 'cursor-wait' : 'disabled:cursor-not-allowed disabled:opacity-60'
                  }`}
                  title={isApproving ? 'Approving...' : 'Approve'}
                >
                  {isApproving
                    ? <Loader2 className="w-5 h-5 animate-spin" />
                    : <CheckCircle2 className="w-5 h-5" />}
                </button>
              )}
              {onReject && (
                <button
                  onClick={handleRejectClick}
                  disabled={isApproving || actionsDisabled}
                  className="p-2.5 min-h-[44px] min-w-[44px] flex items-center justify-center text-gray-600 bg-gray-100 enabled:hover:bg-gray-200 rounded transition-colors disabled:cursor-not-allowed disabled:opacity-60"
                  title="Reject"
                >
                  <XCircle className="w-5 h-5" />
                </button>
              )}
            </div>
          )}

          {/* Remove button for approved codes */}
          {status === 'approved' && onRemove && (
            <button
              onClick={onRemove}
              className="p-2.5 min-h-[44px] min-w-[44px] flex items-center justify-center text-red-600 hover:bg-red-50 rounded transition-colors shrink-0"
              title="Remove"
            >
              <XCircle className="w-5 h-5" />
            </button>
          )}
        </div>
      </motion.div>

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-lg p-5 max-w-md w-full mx-4 shadow-xl"
          >
            <h3 className="text-lg font-semibold mb-2">Reject Code {code}</h3>
            <p className="text-sm text-gray-600 mb-3">
              Please provide a reason for rejection:
            </p>
            <textarea
              value={rejectNotes}
              onChange={(e) => setRejectNotes(e.target.value)}
              placeholder="Enter rejection reason..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mb-4 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              rows={3}
              autoFocus
            />
            <div className="flex gap-2">
              <button
                onClick={handleRejectSubmit}
                disabled={!rejectNotes.trim()}
                className="flex-1 px-4 py-2 min-h-[44px] text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Confirm Rejection
              </button>
              <button
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectNotes('');
                }}
                className="flex-1 px-4 py-2 min-h-[44px] text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg"
              >
                Cancel
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </>
  );
};

/**
 * UnifiedCodeList - Renders a list of codes with approved/suggested separation
 */
export interface UnifiedCodeListProps {
  codes: Array<{
    /** Unique identifier for this item — defaults to `code` if not provided */
    id?: string;
    code: string;
    description: string;
    confidence: number;
    source: 'imo' | 'imo-precision' | 'llm' | 'ai';
    category?: string;
    rationale?: string;
    llmRationale?: string;
    evidenceText?: string;
    evidenceSource?: string;
    status: 'suggested' | 'pending' | 'approved' | 'rejected';
    codeType?: 'icd10' | 'cpt' | 'em';
  }>;
  onApprove?: (id: string) => void;
  onReject?: (id: string, notes?: string) => void;
  onRemove?: (id: string) => void;
  requireRejectNotes?: boolean;
  showEvidence?: boolean;
  showApprovedFirst?: boolean;
  emptyMessage?: string;
  /** id of the code whose approve is saving (spinner on that card) */
  approvingId?: string | null;
  /** Disable approve/reject on every card */
  actionsDisabled?: boolean;
}

export const UnifiedCodeList: React.FC<UnifiedCodeListProps> = ({
  codes,
  onApprove,
  onReject,
  onRemove,
  requireRejectNotes = false,
  showEvidence = false,
  showApprovedFirst = true,
  emptyMessage = 'No codes to display',
  approvingId = null,
  actionsDisabled = false
}) => {
  if (codes.length === 0) {
    return (
      <div className="text-center py-6 text-gray-500 text-sm">
        {emptyMessage}
      </div>
    );
  }

  // Separate approved and pending codes
  const approvedCodes = codes.filter(c => c.status === 'approved');
  const pendingCodes = codes.filter(c => c.status === 'pending' || c.status === 'suggested');
  const rejectedCodes = codes.filter(c => c.status === 'rejected');

  const renderCodeCard = (code: typeof codes[0], index: number) => {
    const itemId = code.id || code.code;
    return (
      <UnifiedCodeCard
        key={`${itemId}-${index}`}
        code={code.code}
        description={code.description}
        confidence={code.confidence}
        source={code.source}
        category={code.category}
        rationale={code.rationale || code.llmRationale}
        evidenceText={code.evidenceText}
        evidenceSource={code.evidenceSource}
        status={code.status}
        codeType={code.codeType}
        onApprove={onApprove ? () => onApprove(itemId) : undefined}
        onReject={onReject ? (notes) => onReject(itemId, notes) : undefined}
        onRemove={onRemove ? () => onRemove(itemId) : undefined}
        requireRejectNotes={requireRejectNotes}
        showEvidence={showEvidence}
        isApproving={approvingId === itemId}
        actionsDisabled={actionsDisabled}
      />
    );
  };

  return (
    <div className="space-y-4">
      {/* Approved Codes Section */}
      {showApprovedFirst && approvedCodes.length > 0 && (
        <div>
          <h4 className="text-xs font-semibold text-green-700 mb-2 flex items-center gap-1">
            <CheckCircle2 className="w-3.5 h-3.5" />
            Approved ({approvedCodes.length})
          </h4>
          <div className="space-y-2">
            <AnimatePresence>
              {approvedCodes.map(renderCodeCard)}
            </AnimatePresence>
          </div>
        </div>
      )}

      {/* Pending/Suggested Codes Section */}
      {pendingCodes.length > 0 && (
        <div>
          {showApprovedFirst && approvedCodes.length > 0 && (
            <h4 className="text-xs font-semibold text-gray-500 mb-2">
              Suggestions ({pendingCodes.length})
            </h4>
          )}
          <div className="space-y-2">
            <AnimatePresence>
              {pendingCodes.map(renderCodeCard)}
            </AnimatePresence>
          </div>
        </div>
      )}

      {/* Rejected Codes Section (collapsed) */}
      {rejectedCodes.length > 0 && (
        <details className="text-xs">
          <summary className="cursor-pointer text-gray-400 hover:text-gray-600">
            Rejected ({rejectedCodes.length})
          </summary>
          <div className="mt-2 space-y-2">
            {rejectedCodes.map(renderCodeCard)}
          </div>
        </details>
      )}
    </div>
  );
};

export default UnifiedCodeCard;
